import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'react-qr-code';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getFrontendSettingsGql } from '../checkout/gql';
import GlobalStyles from '../checkout/components/GlobalStyles';
import { KIOSK_SESSION_KEY } from '../../hooks/useKioskIdleReset';

export default function KioskPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('kiosk');
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const { data, loading } = useQuery(getFrontendSettingsGql, {
    skip: !slug,
    variables: { slug },
  });

  const event = data?.getDetails?.event;
  const frontendSettings = event?.frontendSettings || {};

  const checkoutUrl = (source) => `${window.location.origin}/${slug}/checkout?utm_source=${source}&utm_medium=${source}`;

  const handleBuyHere = () => {
    try {
      window.sessionStorage.setItem(KIOSK_SESSION_KEY, 'true');
    } catch (error) {
      // Ignore storage failures.
    }
    navigate(`/${slug}/checkout?utm_source=kiosk-device&utm_medium=kiosk`);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        {frontendSettings.pageTitle ? <title>{frontendSettings.pageTitle}</title> : null}
        {frontendSettings.assets?.favicon ? <link rel="icon" href={frontendSettings.assets.favicon} /> : null}
      </Helmet>
      <GlobalStyles colors={event?.colors} customCss={frontendSettings.customCss} fonts={frontendSettings.fonts} />
      <Box
        sx={{
          height: '100dvh',
          width: '100vw',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          textAlign: 'center',
          gap: 4,
          p: 4,
        }}
      >
        {event?.logoUrl && (
          <Box
            component="img"
            src={event.logoUrl}
            alt="logo"
            sx={{ maxHeight: 64, maxWidth: 280, objectFit: 'contain' }}
          />
        )}

        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('headline')}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleBuyHere}
          sx={{
            px: 6,
            py: 2,
            fontSize: '1.25rem',
            fontWeight: 700,
            borderRadius: 2,
          }}
        >
          {t('buyHere')}
        </Button>
      </Box>

      <Box
        onClick={() => setQrModalOpen(true)}
        sx={{
          position: 'fixed',
          left: { xs: 16, md: 32 },
          bottom: { xs: 16, md: 32 },
          width: 108,
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: 3,
          p: 1.25,
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <QRCode value={checkoutUrl('kiosk-qr')} size={84} style={{ width: '100%', height: 'auto' }} />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.75,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.65rem',
            color: 'text.secondary',
          }}
        >
          {t('scanLabel')}
        </Typography>
      </Box>

      <Dialog
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { position: 'relative' } }}
      >
        <IconButton
          onClick={() => setQrModalOpen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
            py: 5,
          }}
        >
          <Typography variant="h6">{t('modalTitle')}</Typography>
          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, boxShadow: 3, lineHeight: 0 }}>
            <QRCode value={checkoutUrl('kiosk-qr')} size={220} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
            {t('modalInstruction')}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
